// Angular modules.
import { Component, OnChanges, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
// Zenklub enums.
import { ESidebarMode } from 'app/enums/sidebarmode.enum';
// Zenklub interfaces.
import { IAvailability } from 'app/interfaces/availability.interface';
import { IDay } from 'app/interfaces/calendar.interface';
import { IEvent } from 'app/interfaces/event.interface';
import {
	AppointmentSidebarFrom,
	ISidebar,
} from 'app/interfaces/sidebar.interface';
// Zenklub services.
import { AvailabilitiesService } from 'app/services/availabilities.service';
import { CalendarService } from 'app/services/calendar.service';
import { DateTimeService } from 'app/services/datetime.service';
import { EventsService } from 'app/services/events.service';
import { GlobalService } from 'app/services/global.service';
import { NotificationsService } from 'app/services/notifications.service';
import { UserService } from 'app/services/user.service';
import { enterAnimation } from 'app/utils/animations.util';
import { DateTime } from 'luxon';
import { AppointmentSidebarService } from 'modules/appointments/services/appointment-sidebar.service';
// Third-party modules.
import { ReplaySubject } from 'rxjs';
// Enum for mobile modes.
enum EAvMobileMode {
	Add,
	Edit,
	List,
}
@Component({
	selector: 'app-availabilities',
	templateUrl: './availabilities.component.html',
	styleUrls: ['./availabilities.component.scss'],
	animations: [enterAnimation],
	providers: [EventsService, GlobalService],
})
export class AvailabilitiesComponent implements OnChanges, OnInit {
	// Screen mode.
	editMode = false;
	// Mobile related.
	isMobile = false;
	mobileMode: EAvMobileMode = EAvMobileMode.List;
	sectionTitle: string;
	onCloseGoTo = 'availabilities';
	// Add/edit data.
	availabilityForEdit: IAvailability;
	newPeriodDateFromD: Date = null;
	newPeriodDateToD: Date = null;
	isAddNew = false;
	// Today data.
	_today: IDay = {
		date: new Date(),
		day: 1,
		month: 1,
		monthShortName: 'Jan',
		monthFullName: 'Janeiro',
		year: 2017,
		weekDay: 0,
		weekDayAbbName: 'Dom',
		weekDayShortName: 'Domingo',
		weekDayFullName: 'Domingo',
	};
	// Active data.
	_activeDate: IDay = this._today;
	activeSidebar: ISidebar;
	modalVisible: ESidebarMode;
	eventsListDate: Date;
	eventsForToday: IEvent[] = [];
	sidebarVisible: string;
	// Event fields.
	selectedEventType = '';
	eventTypes = [{ label: 'Loading...', value: 'Loading...' }];
	// Timezones.
	currentTimezone: string;
	slotTimezone: string;
	// If user wants to add new event.
	addNewEvent = false;
	hoursInterval: string[];
	newSessionDate: Date;
	newSessionHour: string;
	// Selected event.
	selectedEvent: IEvent;
	// Calendar data.
	calendarLabels: any;
	// Tabs.
	selectedTab = 'availability';
	// Window data.
	innerHeight: number;
	innerWidth: number;
	calendarHeight: number;
	public resetProfileCompletion: ReplaySubject<boolean> =
		new ReplaySubject<boolean>();
	public showHideDetails: ReplaySubject<boolean> = new ReplaySubject<boolean>();
	// Constructor method.
	constructor(
		protected availabilities: AvailabilitiesService,
		protected calendar: CalendarService,
		protected dateTime: DateTimeService,
		protected events: EventsService,
		protected global: GlobalService,
		protected notifications: NotificationsService,
		protected route: ActivatedRoute,
		protected router: Router,
		protected translate: TranslateService,
		protected userService: UserService,
		public sidebarService: AppointmentSidebarService
	) {
		// Get window size.
		this.innerHeight =
			document.documentElement.clientHeight || window.screen.height;
		this.innerWidth =
			document.documentElement.clientWidth || window.screen.width;
		this.resetProfileCompletion.next(true);
		// Mobile title.
		setTimeout(() => {
			this.setSectionTitleMobile();
		}, 300);
	}

	// On init.
	ngOnInit() {
		// Verify if is a mobile device.
		this.isMobile = this.global.isMobileDevice();
		// List availabilities.
		if (this.isMobile) {
			this.listAvailabilitiesMobile();
		}
		// Text timezone info.
		this.currentTimezone = this.userService.getTimezone();
		// Build active sidebar.
		this.activeSidebar = {
			date: new Date(),
			id: null,
			mode: ESidebarMode.Appointments,
		};
		// Set default sidebar.
		this.sidebarVisible = 'Appointments';
		// Subscription for new show event order.
		this.notifications.getEventToShowDetails().subscribe((eventId) => {
			// Active sidebar.
			this.activeSidebar = {
				date: new Date(),
				id: eventId,
				mode: ESidebarMode.Details,
			};
			this.changeActiveSidebar(this.activeSidebar);
		});
	}

	// On changes.
	public ngOnChanges(): void {
		// Update section title.
		if (this.isMobile) {
			this.setSectionTitleMobile();
		}
	}

	// Set section title.
	public setSectionTitleMobile(): void {
		switch (this.mobileMode) {
			case EAvMobileMode.Add:
				this.sectionTitle = this.translate.instant(
					'OPERATIONS.ADDAVAILABILITY'
				);
				break;
			case EAvMobileMode.Edit:
				this.sectionTitle = this.translate.instant(
					'OPERATIONS.EDITAVAILABILITY'
				);
				break;
			default:
				this.sectionTitle = this.translate.instant(
					'GENERAL.MANAGEAVAILABILITIES'
				);
				break;
		}
	}

	// Add new availability on mobile.
	public addNewAvailabilityMobile(): void {
		this.availabilityForEdit = null;
		this.mobileMode = EAvMobileMode.Add;
		this.setSectionTitleMobile();
		this.onCloseGoTo = 'availabilities';
	}

	// Edit an availability on mobile.
	public editAvailabityMobile(availability: IAvailability): void {
		this.availabilityForEdit = availability;
		this.mobileMode = EAvMobileMode.Edit;
		this.setSectionTitleMobile();
		this.onCloseGoTo = 'availabilities';
	}

	// List current availability on mobile.
	public listAvailabilitiesMobile(): void {
		this.availabilityForEdit = null;
		this.mobileMode = EAvMobileMode.List;
		this.setSectionTitleMobile();
		this.onCloseGoTo = 'appointments';
	}

	// Cancel edit on mobile.
	public cancelEditMobile(): void {
		this.goToPage('availabilities');
	}

	// Reschedule.
	public reschedule(sb: ISidebar): void {
		sb.mode = ESidebarMode.Edit;
		this.changeActiveSidebar(sb);
	}

	// Change active sidebar.
	private async changeActiveSidebar(sidebar: ISidebar) {
		this.activeSidebar = {
			...sidebar,
			time: sidebar.hour
				? this.getTimeByString(sidebar.date, sidebar.hour)
				: null,
			id: sidebar.id ?? null,
			contactId: sidebar.contactId ?? null,
		};

		this.sidebarVisible = 'Appointments';
		this.modalVisible = sidebar.mode;

		this.showHideDetails.next(
			this.sidebarVisible !== 'Appointments' || !this.sidebarVisible
		);

		if (
			this.activeSidebar.mode === ESidebarMode.New ||
			this.activeSidebar.mode === ESidebarMode.Edit
		) {
			await this.sidebarService.setSidebar(true, this.activeSidebar);
		}
	}

	public getTimeByString(date, hour) {
		const _date = DateTime.fromJSDate(date);
		const _hour = DateTime.fromFormat(hour, 'HH:mm');

		_date.set({ hour: _hour.hour, minute: _hour.minute, second: 0 });
		return _date.toJSDate();
	}
	// Add new availability
	public addNew(): void {
		const data: any = {
			isEdit: true,
			item: null,
			addNew: true,
		};
		this.changeScreenMode(data);
	}

	// List current availabilities.
	public listAll(): void {
		const data: any = {
			isEdit: false,
			item: null,
			isAddNew: false,
		};
		this.changeScreenMode(data);
	}

	// Change screen mode.
	changeScreenMode(data: any): void {
		const item: IAvailability = data.item;
		const addNew: boolean = data.addNew;

		this.editMode = data.isEdit;

		if (item && item.startDate && item.endDate) {
			this.availabilityForEdit = item;
			this.slotTimezone = item.timezone;
			this.isAddNew = addNew;
			this.newPeriodDateFromD = DateTime.fromISO(item.startDate, {
				zone: item.timezone,
			}).toJSDate();

			this.newPeriodDateToD = DateTime.fromISO(item.endDate, {
				zone: item.timezone,
			}).toJSDate();
		} else {
			const today = new Date();
			const nextMonth = new Date().setMonth(today.getMonth() + 1);
			// Get intervals.
			this.availabilityForEdit = null;
			this.slotTimezone = this.currentTimezone;
			this.isAddNew = false;
			this.newPeriodDateFromD = today;
			this.newPeriodDateToD = new Date(nextMonth);
		}
	}

	// Arrange components on screen.
	public onResize(): void {
		const mainTitleContainer: JQuery<HTMLElement> = $('#config-newAppointment');
		const mainTitle: JQuery<HTMLElement> = $('#avMainTitle');
		if (mainTitleContainer) {
			const mtcW: number = mainTitleContainer.width();
			mainTitle.css('width', mtcW - 115);
		}
	}

	// Navigate.
	public goToPage(page: string): void {
		switch (page.toLowerCase()) {
			// Home =====================================================================
			case '/':
			case 'calendar':
			case 'dashboard':
			case 'home':
				this.router.navigate(['/calendar']);
				break;
			case 'appointments':
				this.router.navigate(['/appointments']);
				break;
			case 'availabilities':
				if (this.isMobile) {
					this.listAvailabilitiesMobile();
				} else {
					this.router.navigate(['/availabilities']);
				}
				break;
		}
	}

	public createNewEvent(sidebar: ISidebar): void {
		this.activeSidebar = {
			mode: ESidebarMode.New,
			date: new Date(),
			contactId: sidebar.contactId,
			from: AppointmentSidebarFrom.HOME,
		};

		this.changeActiveSidebar(this.activeSidebar);
	}

	// Force to refresh local events.
	protected forceToRefreshEvents(force: boolean): void {
		if (force) {
			this.events.clearEventsCache();
		}
		this.events.updateLocalEvents({ setWeek: true });
	}
}
